import React, { useEffect, useRef, useState } from 'react';
import ModeSelector from './ModeSelector';
import './ChatComposer.css';

function ChatComposer({
  composerRef,
  isComposerPreviewDisabled,
  isOarsMode,
  isStrategiesMode = false,
  handleOarsSubmit,
  handleStrategiesSubmit,
  handleSendMessage,
  handleForceEndSession,
  isEndSessionDisabled,
  handleRestartSession,
  isRestartSessionDisabled,
  voiceMode,
  voiceStatus,
  handleEnterVoiceMode,
  isClientMode,
  isVoiceModeDisabled,
  showComposerSelectionCue,
  showComposerLimitFlash,
  inputRef,
  inputMessage,
  setInputMessage,
  handleInputKeyDown,
  oarsExercise,
  oarsResult,
  oarsSubmitting,
  llmMode,
  counsellorLabel,
  sessionEnded,
  endReason,
  isConsentPending,
  sessionId,
  t,
  isOarsInputDisabled,
  isStrategiesInputDisabled = true,
  strategiesInputPlaceholder = 'Select a strategy to begin.',
  isTextInputBlocked,
  setKeyboardOpen,
  isSendDisabled,
  overlayBottomOffset,
  llmPaused,
  llmFinishingUtterance,
  handlePauseLlmMode,
  showBtcConsentCue,
  handleConsentChoice,
  modeBarVisible,
  setModeBarVisible,
  activeMode,
  onFreeForm,
  onBeTheClient,
  onOars,
  onStrategies,
  hideModeSelector = false,
  compactSessionControls = false,
}) {
  const [sessionControlsOpen, setSessionControlsOpen] = useState(false);
  const sessionControlsRef = useRef(null);
  const overlayClassName = `chat-overlay-stack${!hideModeSelector && !modeBarVisible ? ' chat-overlay-stack--mode-collapsed' : ''}`;
  const isPracticeMode = isOarsMode || isStrategiesMode;
  const endSessionTitle = isPracticeMode ? 'Not available in practice mode' : 'End session';
  const restartTitle = isPracticeMode ? 'Not available in practice mode' : 'Restart session';
  const voiceTitle = isPracticeMode
    ? 'Voice mode is not available in this practice mode'
    : (voiceMode ? `Voice mode: ${voiceStatus}` : 'Enter voice mode (one-way)');

  useEffect(() => {
    if (!compactSessionControls) {
      setSessionControlsOpen(false);
    }
  }, [compactSessionControls]);

  useEffect(() => {
    if (!sessionControlsOpen) return undefined;

    const handlePointerDown = (event) => {
      if (sessionControlsRef.current && !sessionControlsRef.current.contains(event.target)) {
        setSessionControlsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [sessionControlsOpen]);

  const endSessionIcon = (
    <svg
      className="control-icon"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      role="img"
      aria-hidden="true"
    >
      <path d="M7 7h10v10H7z" />
    </svg>
  );
  const restartIcon = (
    <svg
      className="control-icon"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      role="img"
      aria-hidden="true"
    >
      <path d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/>
      <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
    </svg>
  );
  const voiceIcon = (
    <svg className="control-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.4 5.9C.55 7.8-.5 10.3-.5 13s1.05 5.2 2.9 7.1"/>
      <path d="M4.25 7.5c-1.5 1.45-2.35 3.4-2.35 5.5s.85 4.05 2.35 5.5"/>
      <path d="M19.75 7.5c1.5 1.45 2.35 3.4 2.35 5.5s-.85 4.05-2.35 5.5"/>
      <path d="M21.6 5.9c1.85 1.9 2.9 4.4 2.9 7.1s-1.05 5.2-2.9 7.1"/>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M6.5 11a5.5 5.5 0 0 0 11 0"/>
      <path d="M12 16.5V20"/>
      <path d="M9 20h6"/>
    </svg>
  );
  const pauseToggleIcon = llmPaused ? (
    <svg className="pause-icon" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M5 3.2v9.6l7-4.8-7-4.8z" fill="currentColor" />
    </svg>
  ) : (
    <svg className="pause-icon" viewBox="0 0 16 16" aria-hidden="true">
      <rect x="3.25" y="2.5" width="3" height="11" rx="0.8" fill="currentColor" />
      <rect x="9.75" y="2.5" width="3" height="11" rx="0.8" fill="currentColor" />
    </svg>
  );

  const sessionControlOptions = [
    {
      key: 'end',
      label: 'End Session',
      icon: endSessionIcon,
      disabled: isEndSessionDisabled,
      title: endSessionTitle,
      onClick: isPracticeMode ? undefined : handleForceEndSession,
    },
    {
      key: 'restart',
      label: 'Restart Session',
      icon: restartIcon,
      disabled: isRestartSessionDisabled,
      title: restartTitle,
      onClick: isPracticeMode ? undefined : handleRestartSession,
    },
    {
      key: 'voice',
      label: voiceMode ? 'Voice Active' : 'Voice Mode',
      icon: voiceIcon,
      disabled: isVoiceModeDisabled,
      title: voiceTitle,
      onClick: isPracticeMode ? undefined : handleEnterVoiceMode,
      active: voiceMode,
    },
  ];

  return (
    <div className="chat-bottom-stack">
      <div className={overlayClassName} style={{ bottom: `${overlayBottomOffset}px` }}>
        {showComposerLimitFlash && (
          <div className="composer-limit-warning" aria-live="polite">
            Response Exceeded 1000 characters
          </div>
        )}
        {llmMode && !sessionEnded && (
          <div
            className={`llm-pause-container${!hideModeSelector && !modeBarVisible ? ' llm-pause-container--mode-collapsed' : ''}`}
            aria-live="polite"
          >
            <button
              type="button"
              className={`llm-pause-btn ${llmPaused ? 'paused' : ''}`}
              onClick={handlePauseLlmMode}
              title={llmPaused ? 'Resume LLM conversation' : 'Pause LLM conversation'}
              aria-pressed={llmPaused}
            >
              {pauseToggleIcon}
              <span className="pause-text">{llmPaused ? 'Resume' : 'Pause'}</span>
            </button>
          </div>
        )}

        {!hideModeSelector && (
          <ModeSelector modeBarVisible={modeBarVisible} setModeBarVisible={setModeBarVisible} activeMode={activeMode} onFreeForm={onFreeForm} onBeTheClient={onBeTheClient} onOars={onOars} onStrategies={onStrategies} />
        )}
      </div>

      <form
        ref={composerRef}
        className={`chat-input-container${isComposerPreviewDisabled ? ' disabled-preview' : ''}`}
        onSubmit={isStrategiesMode ? handleStrategiesSubmit : isOarsMode ? handleOarsSubmit : handleSendMessage}
      >
        {compactSessionControls ? (
          <div className="session-controls-compact" ref={sessionControlsRef}>
            <button
              type="button"
              className="session-controls-compact__trigger"
              aria-label="Session controls"
              aria-haspopup="menu"
              aria-expanded={sessionControlsOpen}
              onClick={() => setSessionControlsOpen((open) => !open)}
            >
              <span className="session-controls-compact__dot" />
              <span className="session-controls-compact__dot" />
              <span className="session-controls-compact__dot" />
            </button>
            {sessionControlsOpen && (
              <div className="session-controls-compact__menu" role="menu">
                {sessionControlOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    role="menuitem"
                    className={`session-controls-compact__item${option.active ? ' active' : ''}`}
                    disabled={option.disabled}
                    title={option.title}
                    onClick={() => {
                      if (option.disabled || !option.onClick) return;
                      option.onClick();
                      setSessionControlsOpen(false);
                    }}
                  >
                    {option.icon}
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={isPracticeMode ? undefined : handleForceEndSession}
              disabled={isEndSessionDisabled}
              className="session-control-btn end-session-btn"
              title={endSessionTitle}
              aria-label="End session"
            >
              {endSessionIcon}
            </button>
            <button
              type="button"
              onClick={isPracticeMode ? undefined : handleRestartSession}
              disabled={isRestartSessionDisabled}
              className="session-control-btn restart-btn"
              title={restartTitle}
              aria-label="Restart session"
            >
              {restartIcon}
            </button>
            <button
              type="button"
              className={`session-control-btn voice-mode-btn${voiceMode ? ` voice-mode-btn--active voice-mode-btn--${voiceStatus}` : ''}`}
              onClick={!isPracticeMode ? handleEnterVoiceMode : undefined}
              disabled={isVoiceModeDisabled}
              aria-label={voiceMode ? `Voice mode: ${voiceStatus}` : 'Enter voice mode'}
              title={voiceTitle}
            >
              {voiceIcon}
            </button>
          </>
        )}
        <div
          className={`chat-input-shell${showComposerSelectionCue ? ' chat-input-shell--selection-cue' : ''}${showComposerLimitFlash ? ' chat-input-shell--limit-flash' : ''}`}
          data-guided-highlight-purpose={showComposerSelectionCue ? 'Write your response here to continue the practice.' : undefined}
        >
          <textarea
            ref={inputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder={
              isStrategiesMode
                ? strategiesInputPlaceholder
                : isOarsMode
                ? (!oarsExercise ? t('chat_placeholder_loading') : oarsResult ? t('chat_placeholder_oars_done') : oarsSubmitting ? t('chat_placeholder_evaluating') : t('chat_placeholder_oars_input'))
                : llmMode
                ? t('chat_placeholder_llm_running', { label: counsellorLabel })
                : sessionEnded
                ? t('chat_placeholder_session_ended', { reason: endReason })
                : isConsentPending
                ? t('chat_placeholder_consent')
                : isClientMode
                ? (sessionId ? t('chat_placeholder_client_active') : t('chat_placeholder_client_start'))
                : t('chat_placeholder_counsellor')
            }
            disabled={isStrategiesMode ? isStrategiesInputDisabled : isOarsMode ? isOarsInputDisabled : isTextInputBlocked}
            className="chat-input"
            rows={1}
          />
          <button
            type="button"
            className="keyboard-btn"
            aria-label="Virtual keyboard"
            title="Virtual keyboard"
            onClick={() => setKeyboardOpen((prev) => !prev)}
            disabled={isStrategiesMode ? isStrategiesInputDisabled : isOarsMode ? isOarsInputDisabled : isTextInputBlocked}
          >
            <svg
              className="keyboard-icon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="2.5" y="5" width="19" height="14" rx="3" />
              <path d="M6 9h.01M9.5 9h.01M13 9h.01M16.5 9h.01M6 12.5h.01M9.5 12.5h.01M13 12.5h.01M16.5 12.5h.01" />
              <path d="M7 16h10" />
            </svg>
          </button>
          <button
            type="submit"
            disabled={isSendDisabled}
            className="send-btn send-btn--compact-inside"
            aria-label="Send message"
            title="Send message"
          >
            <svg
              className="send-icon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              role="img"
              aria-hidden="true"
            >
              <path d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
          </button>
        </div>
        <button
          type="submit"
          disabled={isSendDisabled}
          className="send-btn send-btn--desktop"
          aria-label="Send message"
          title="Send message"
        >
          <svg
            className="send-icon"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            role="img"
            aria-hidden="true"
          >
            <path d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z" />
          </svg>
        </button>
      </form>
    </div>
  );
}

export default ChatComposer;
